import { Question, QuestionOption } from "@/data/questions";
import { cn } from "@/lib/utils";
import { CheckCircle2, AlertCircle, ExternalLink, Users, ThumbsUp, Calendar, MapPin, Building2, Briefcase, Globe } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { Language } from "@/hooks/use-browser-language";
import { getQuizTranslation, questionKeyMap, optionKeyMap } from "@/i18n/quiz";

interface QuestionCardProps {
  question: Question;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  language?: Language;
}

const validateDomain = (domain: string): { isValid: boolean; error?: string; suggestion?: string } => {
  if (!domain || domain.trim() === '') {
    return { isValid: true }; // Empty is ok (optional field)
  }

  const trimmed = domain.trim();
  
  // Check for common mistakes
  if (trimmed.includes(' ')) {
    const fixed = trimmed.replace(/\s+/g, '.');
    return { 
      isValid: false, 
      error: 'Domena nie może zawierać spacji',
      suggestion: fixed
    };
  }

  // Remove protocol if present
  let withoutProtocol = trimmed.replace(/^https?:\/\//, '');
  
  // Remove trailing slashes
  withoutProtocol = withoutProtocol.replace(/\/+$/, '');
  
  // Basic domain validation - must have at least one dot
  if (!withoutProtocol.includes('.')) {
    return { 
      isValid: false, 
      error: 'Domena musi zawierać przynajmniej jedną kropkę (np. firma.pl)'
    };
  }

  // Check for valid characters (alphanumeric, dots, hyphens)
  const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
  if (!domainRegex.test(withoutProtocol)) {
    return { 
      isValid: false, 
      error: 'Nieprawidłowy format domeny. Użyj formatu: firma.pl lub www.firma.pl'
    };
  }

  return { isValid: true };
};

export const QuestionCard = ({ question, value, onChange, language = "pl" }: QuestionCardProps) => {
  const [domainError, setDomainError] = useState<{ error?: string; suggestion?: string }>({});
  const t = getQuizTranslation(language);
  
  // Get translated question text
  const questionKey = questionKeyMap[question.id];
  const translatedQuestion = questionKey ? t(questionKey) : question.question;
  
  // Get translated option label
  const getOptionLabel = (option: QuestionOption) => {
    // For dynamic options (Q16 competitors), use original labels
    if (question.isDynamic) return option.label;
    
    // Try to find translation key for this option
    const optionTranslationKey = optionKeyMap[option.value];
    if (optionTranslationKey) {
      return t(optionTranslationKey);
    }
    return option.label;
  };
  
  const handleSingleSelect = (optionValue: string) => {
    onChange(optionValue);
  };
  
  const handleMultipleSelect = (optionValue: string) => {
    const currentValues = Array.isArray(value) ? value : [];
    if (currentValues.includes(optionValue)) {
      onChange(currentValues.filter(v => v !== optionValue));
    } else {
      onChange([...currentValues, optionValue]);
    }
  };
  
  const handleDomainChange = (newValue: string) => {
    // Auto-fix: remove protocol
    let cleaned = newValue.replace(/^https?:\/\//, '');
    
    // Auto-fix: remove trailing slashes
    cleaned = cleaned.replace(/\/+$/, '');
    
    // Auto-fix: replace spaces with dots (common mistake like "www mdr-biuro.pl")
    if (cleaned.includes(' ')) {
      cleaned = cleaned.replace(/\s+/g, '.');
    }
    
    onChange(cleaned);
    
    // Validate after a short delay (debounce)
    if (question.id === 4) {
      const validation = validateDomain(cleaned);
      setDomainError({ error: validation.error, suggestion: validation.suggestion });
    }
  };
  
  const isSelected = (optionValue: string) => {
    if (question.type === 'single') {
      return value === optionValue;
    }
    return Array.isArray(value) && value.includes(optionValue);
  };
  
  return (
    <div className="w-full max-w-2xl mx-auto">
      <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
        {translatedQuestion}
      </h2>
      
      {/* Special display for LinkedIn profile question with Bright Data enrichment */}
      {question.id === 17 && question.metadata?.enrichedData && (
        <div className="mb-8 p-6 bg-gradient-to-br from-card to-secondary border border-border rounded-xl shadow-lg">
          {/* Header: Logo + Nazwa + Link */}
          <div className="flex items-start gap-4 mb-6 pb-6 border-b">
            {question.metadata.logo && (
              <img 
                src={question.metadata.logo}
                alt={question.metadata.companyName}
                className="w-20 h-20 rounded-lg object-contain bg-white p-2 shadow-sm"
              />
            )}
            <div className="flex-1">
              <h3 className="font-bold text-xl mb-2">
                {question.metadata.companyName}
              </h3>
              <a 
                href={question.metadata.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                {language === 'en' ? 'View LinkedIn profile' : 'Zobacz profil na LinkedIn'}
              </a>
            </div>
          </div>

          {/* Stats Grid */}
          {question.metadata.enrichedData.enriched && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {question.metadata.enrichedData.employeeCount && (
                <div className="text-center p-3 bg-background rounded-lg">
                  <Users className="w-5 h-5 mx-auto mb-1 text-primary" />
                  <div className="text-2xl font-bold">{question.metadata.enrichedData.employeeCount}</div>
                  <div className="text-xs text-muted-foreground">{language === 'en' ? 'Employees' : 'Pracowników'}</div>
                </div>
              )}
              
              {question.metadata.enrichedData.followerCount && (
                <div className="text-center p-3 bg-background rounded-lg">
                  <ThumbsUp className="w-5 h-5 mx-auto mb-1 text-primary" />
                  <div className="text-2xl font-bold">{question.metadata.enrichedData.followerCount}</div>
                  <div className="text-xs text-muted-foreground">{language === 'en' ? 'Followers' : 'Obserwujących'}</div>
                </div>
              )}
              
              {question.metadata.enrichedData.founded && (
                <div className="text-center p-3 bg-background rounded-lg">
                  <Calendar className="w-5 h-5 mx-auto mb-1 text-primary" />
                  <div className="text-2xl font-bold">{question.metadata.enrichedData.founded}</div>
                  <div className="text-xs text-muted-foreground">{language === 'en' ? 'Founded' : 'Założona'}</div>
                </div>
              )}
              
              {question.metadata.enrichedData.headquarters && (
                <div className="text-center p-3 bg-background rounded-lg">
                  <MapPin className="w-5 h-5 mx-auto mb-1 text-primary" />
                  <div className="text-sm font-semibold truncate">{question.metadata.enrichedData.headquarters}</div>
                  <div className="text-xs text-muted-foreground">{language === 'en' ? 'Headquarters' : 'Siedziba'}</div>
                </div>
              )}
            </div>
          )}

          {/* About Section */}
          {question.metadata.enrichedData.description && (
            <div className="mb-6">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                {language === 'en' ? 'About' : 'O firmie'}
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed line-clamp-4">
                {question.metadata.enrichedData.description}
              </p>
            </div>
          )}

          {/* Industry & Size */}
          {(question.metadata.enrichedData.industry || question.metadata.enrichedData.company_size) && (
            <div className="flex flex-wrap gap-2 mb-4">
              {question.metadata.enrichedData.industry && (
                <Badge variant="secondary" className="text-xs">
                  <Briefcase className="w-3 h-3 mr-1" />
                  {question.metadata.enrichedData.industry}
                </Badge>
              )}
              {question.metadata.enrichedData.company_size && (
                <Badge variant="secondary" className="text-xs">
                  <Users className="w-3 h-3 mr-1" />
                  {question.metadata.enrichedData.company_size}
                </Badge>
              )}
            </div>
          )}

          {/* Website Link */}
          {question.metadata.enrichedData.website && (
            <a 
              href={question.metadata.enrichedData.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              <Globe className="w-3 h-3" />
              {question.metadata.enrichedData.website}
            </a>
          )}
        </div>
      )}
      
      {/* Fallback dla braku enrichedData - stary widok */}
      {question.id === 17 && question.metadata && !question.metadata.enrichedData && (
        <div className="mb-8 p-6 bg-card border border-border rounded-lg shadow-sm">
          <div className="flex items-center gap-4 mb-4">
            {question.metadata.logo && (
              <img 
                src={question.metadata.logo} 
                alt={question.metadata.companyName || 'Company logo'}
                className="w-16 h-16 rounded object-contain bg-white"
              />
            )}
            <div className="flex-1">
              <h3 className="font-semibold text-lg mb-1">
                {question.metadata.companyName}
              </h3>
              <a 
                href={question.metadata.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline break-all"
              >
                {question.metadata.linkedinUrl}
              </a>
            </div>
          </div>
        </div>
      )}
      
      {question.type === 'multiple' && (
        <p className="text-sm text-red-500 text-center mb-6 font-medium">
          {language === 'en' ? 'You can select multiple answers' : 'Możesz wybrać wiele odpowiedzi'}
        </p>
      )}
      
      {question.type === 'text' ? (
        <div className="max-w-md mx-auto">
          <Input
            type="text"
            placeholder={question.id === 4 ? t("q4_placeholder") : question.placeholder}
            value={value as string}
            onChange={(e) => question.id === 4 ? handleDomainChange(e.target.value) : onChange(e.target.value)}
            className={cn(
              "text-base h-12",
              domainError.error && question.id === 4 && "border-destructive"
            )}
          />
          {question.id === 4 && domainError.error && (
            <div className="mt-2 flex items-start gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p>{domainError.error}</p>
                {domainError.suggestion && (
                  <p className="text-muted-foreground mt-1">
                    {language === 'en' ? 'Did you mean: ' : 'Czy chodziło o: '}
                    <span className="font-medium text-foreground">{domainError.suggestion}</span>?
                  </p>
                )}
              </div>
            </div>
          )}
          {question.id === 4 && !domainError.error && (
            <p className="text-sm text-muted-foreground mt-2 text-center">
              {language === 'en' 
                ? 'Enter domain without https:// (e.g. company.com or www.company.com)'
                : 'Podaj domenę bez https:// (np. firma.pl lub www.firma.pl)'}
            </p>
          )}
          {question.id === 41 && (
            <p className="text-sm text-muted-foreground mt-2 text-center">
              {language === 'en'
                ? 'Enter address without https:// (e.g. vectorsynergy.com)'
                : 'Podaj adres bez https:// (np. vectorsynergy.com)'}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {question.options.map((option) => (
          <button
            key={option.value}
            onClick={() => 
              question.type === 'single' 
                ? handleSingleSelect(option.value)
                : handleMultipleSelect(option.value)
            }
            className={cn(
              "w-full p-4 rounded-lg border-2 text-left transition-all duration-200",
              "hover:border-primary hover:shadow-md",
              "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
              isSelected(option.value)
                ? "border-primary bg-secondary shadow-md"
                : "border-border bg-card"
            )}
          >
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <span className="font-medium">{getOptionLabel(option)}</span>
                {option.url && (
                  <span className="text-xs text-muted-foreground">{option.url}</span>
                )}
              </div>
              {isSelected(option.value) && (
                <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
              )}
            </div>
          </button>
        ))}
        </div>
      )}
    </div>
  );
};
